'use strict';

/**
 * @fileoverview The blocklist half of the password policy — the values NIST SP 800-63B-4 §3.1.1.2
 * requires a verifier to refuse because an attacker tries them first: commonly used, expected, or
 * compromised passwords.
 *
 * Three instruments, because a list alone cannot hold the "expected" class:
 *
 * 1. `COMMON_PASSWORDS` — the words attackers try first. Most are shorter than the length floor, so
 *    on their own they are already refused; they matter as the ROOT of a derivative
 *    (`password1234567!`, `passwordpassword`), which is the check `isCommonPassword` actually runs.
 * 2. Keyboard / alphabet / digit RUNS — `qwertyuiopasdfg`, `123456789012345`, `zyxwvutsrqponml`.
 * 3. REPEATS — `aaaaaaaaaaaaaaa`, `abcabcabcabcabc`, `correcthorsecorrecthorse`.
 *
 * Moved here from `@puffin-core/login-shell`'s `utils/passwordStrength.js` (UNREACHABLE-MODULE-
 * BURNDOWN-001 Q-L16-2), where it was one client's private list; a server never consulted it, so a
 * password that screen refused another screen — and every server — accepted.
 *
 * @module @puffin-core/password-policy/commonPasswords
 */

/**
 * The words attackers try first (case-insensitive). SplashData annual breach compilations, NIST
 * guidance and ecosystem audit-log observations. Keep the order stable for diffability; add at the
 * end.
 *
 * A frozen ARRAY, not a Set: `Object.freeze` does not stop `Set#add`, so an exported Set could be
 * extended by any importer. Lookups go through the module-private `COMMON_PASSWORD_SET`.
 *
 * @type {ReadonlyArray<string>}
 */
const COMMON_PASSWORDS = Object.freeze([
  'password',
  '123456',
  '12345678',
  '123456789',
  'qwerty',
  'abc123',
  'letmein',
  'admin',
  'welcome',
  'iloveyou',
  'monkey',
  'dragon',
  'master',
  'sunshine',
  'princess',
  'football',
  'baseball',
  'superman',
  'batman',
  'starwars',
  'trustno1',
  'qwerty123',
  'qwertyuiop',
  'password1',
  'password123',
  '1q2w3e4r',
  '1234567',
  '1234567890',
  '111111',
  '222222',
  '333333',
  '666666',
  '888888',
  '121212',
  '123123',
  '1q2w3e',
  'access',
  'hello',
  'hello123',
  'welcome1',
  'welcome123',
  'login',
  'login123',
  'passw0rd',
  'p@ssword',
  'p@ssw0rd',
  'passw@rd',
  'michael',
  'jennifer',
  'jessica',
  'charlie',
  'thomas',
  'daniel',
  'matthew',
  'joshua',
  'andrew',
  'anthony',
  'robert',
  'richard',
  'shadow',
  'ashley',
  'bailey',
  'hunter',
  'buster',
  'soccer',
  'ninja',
  'tigger',
  'cookie',
  'summer',
  'winter',
  'autumn',
  'spring',
  'freedom',
  'whatever',
  'computer',
  'internet',
  'google',
  'facebook',
  'linkedin',
  'twitter',
  'instagram',
  'amazon',
  'apple',
  'samsung',
  'letmein1',
  'admin123',
  'root',
  'toor',
  'test',
  'test123',
  'guest',
  'demo',
  'change',
  'changeme',
  'temp',
  'temp123',
  'qazwsx',
  'zaq12wsx',
  'asdfgh',
  'asdfghjkl',
  'zxcvbn',
  'zxcvbnm',
  'iloveyouiloveyou',
  'passwordpassword',
  'correcthorsebatterystaple',
]);

/** O(1) lookup over the list above; never exported. */
const COMMON_PASSWORD_SET = new Set(COMMON_PASSWORDS);

/**
 * Character runs an attacker enumerates: the alphabet, the digits, each keyboard row and the
 * keyboard's diagonal columns. A password that is a contiguous stretch of one of these — forwards or
 * backwards, wrapping at the end — is "expected" however long it is.
 *
 * @type {ReadonlyArray<string>}
 */
const PASSWORD_SEQUENCES = Object.freeze([
  'abcdefghijklmnopqrstuvwxyz',
  '0123456789',
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
  'qwertyuiopasdfghjklzxcvbnm',
  '1qaz2wsx3edc4rfv5tgb6yhn7ujm8ik9ol0p',
  '!@#$%^&*()',
]);

/**
 * Is `value` a contiguous stretch of `sequence` repeated end to end (so `9012345…` counts)?
 *
 * @param {string} value
 * @param {string} sequence
 * @returns {boolean}
 */
function isRunOf(value, sequence) {
  const cycled = sequence.repeat(Math.ceil(value.length / sequence.length) + 1);
  return cycled.includes(value);
}

/**
 * Is `value` (at least two characters) a run of any known sequence, forwards or backwards?
 *
 * @param {string} value lower-cased candidate
 * @returns {boolean}
 */
function isSequentialRun(value) {
  if (value.length < 2) return false;
  const reversed = [...value].reverse().join('');
  return PASSWORD_SEQUENCES.some((seq) => isRunOf(value, seq) || isRunOf(reversed, seq));
}

/**
 * Is `value` one shorter unit written out two or more times (`abab`, `passwordpassword`)?
 *
 * @param {string} value
 * @returns {boolean}
 */
function isRepeatedUnit(value) {
  if (value.length < 2) return false;
  // A string is a repetition of a proper prefix exactly when it occurs inside itself doubled at an
  // offset other than 0 and its own length — the classic rotation test.
  return `${value}${value}`.indexOf(value, 1) < value.length;
}

/**
 * Split a lower-cased value into the letters-bounded CORE and the digits/symbols bolted onto either
 * end — the derivative an attacker tries after the root (`password1234567!`, `2024summer2024`).
 *
 * @param {string} lower
 * @returns {{prefix: string, core: string, suffix: string}}
 */
function splitAffixes(lower) {
  const prefix = /^[^a-z]*/.exec(lower)[0];
  const rest = lower.slice(prefix.length);
  const suffix = /[^a-z]*$/.exec(rest)[0];
  return { prefix, core: rest.slice(0, rest.length - suffix.length), suffix };
}

/**
 * One candidate string against the run and repeat instruments.
 *
 * @param {string} value lower-cased
 * @returns {boolean}
 */
function isPatterned(value) {
  return isSequentialRun(value) || isRepeatedUnit(value);
}

/**
 * An affix adds nothing an attacker has to guess when it is a single character or itself a run or
 * a repeat (`!`, `123`, `!!!`).
 *
 * @param {string} affix
 * @returns {boolean}
 */
function isTrivialAffix(affix) {
  return affix.length <= 1 || isPatterned(affix);
}

/**
 * Is this password common, expected, or a trivial derivative of one?
 *
 * - the whole value is a common password, a run or a repeat; or
 * - its letters-bounded core is a COMMON password, whatever digits and symbols surround it
 *   (`password4829175` is a dictionary word plus a guessable tail); or
 * - its core is a run or a repeat AND so is everything bolted onto it (`abcdefghijk1234`). A short
 *   run inside random digits (`ab4829175302649`) is NOT refused — the digits carry the entropy.
 *
 * An EMPTY password is not reported as common — it has not been compared with anything, and the
 * caller's length requirement already refuses it.
 *
 * @param {string} password
 * @returns {boolean}
 */
function isCommonPassword(password) {
  if (password.length === 0) return false;
  const lower = password.toLowerCase();
  if (COMMON_PASSWORD_SET.has(lower) || isPatterned(lower)) return true;
  const { prefix, core, suffix } = splitAffixes(lower);
  if (core.length === 0) return false;
  if (COMMON_PASSWORD_SET.has(core)) return true;
  return isPatterned(core) && isTrivialAffix(prefix) && isTrivialAffix(suffix);
}

module.exports = {
  COMMON_PASSWORDS,
  PASSWORD_SEQUENCES,
  isCommonPassword,
};
